import { useState } from "react";
import { Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/i18n/LanguageContext";
import { getSectorNoun } from "@/lib/sectorNoun";

import type { PlanComparisonLang } from "@/lib/planComparisonContent";

export const PREMIUM_MODULE_COUNT = 2;

type Props = {
  /** Contenido ya resuelto en el idioma activo (BD o valores por defecto). */
  content: PlanComparisonLang;
  onCheckout?: () => Promise<void>;
  /** En la vista previa del back office el botón no lanza el pago. */
  disableCheckout?: boolean;
  retailSector?: string | null | undefined;
};

/**
 * Card "Lite / Club Pro" usada en el chip del usuario y en la vista previa del BO.
 * Estructura fija: card blanca con tabla, frase y CTA. Sin título ni barras extra.
 */
export default function PlanComparisonPanel({ content, onCheckout, disableCheckout, retailSector }: Props) {
  const { t, lang } = useLanguage();

  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const rows = content.rows.map((row) => ({
    ...row,
    pro: row.pro.includes("{count}") ? row.pro.replace("{count}", String(PREMIUM_MODULE_COUNT)) : row.pro,
    help: row.help.replace("{count}", String(PREMIUM_MODULE_COUNT)),
  }));

  const handleCheckout = async () => {
    if (disableCheckout || !onCheckout) return;
    setCheckoutLoading(true);
    try {
      await onCheckout();
    } finally {
      setCheckoutLoading(false);
    }
  };

  const lastIndex = rows.length - 1;
  /** Solo una ayuda abierta a la vez; en móvil se abre y cierra con un toque. */
  const [openHelp, setOpenHelp] = useState<number | null>(null);
  const footer = content.footer.replace(
    "{sector}",
    getSectorNoun(retailSector, lang === "es" ? "es" : "en"),
  );

  return (
    <TooltipProvider delayDuration={150}>
      <div className="font-plan-body relative z-10 max-h-[calc(100dvh-96px)] w-[calc(100vw-24px)] max-w-[440px] overflow-y-auto overflow-x-hidden rounded-2xl border border-plan-popup-border bg-card/95 p-3 pb-3.5 shadow-plan-popup backdrop-blur-sm sm:p-4">
        <table className="w-full table-fixed border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="w-[29%] border-b border-plan-row py-2.5 pr-1.5 text-left sm:pr-2" aria-hidden="true" />
              <th className="w-[24%] border-b border-plan-row px-1.5 py-2.5 text-center text-[11px] font-medium text-muted-foreground sm:px-2">
                Sin plan
              </th>
              <th className="font-plan-heading w-[47%] rounded-t-xl bg-plan-pro-header px-2 py-2.5 text-center text-[11px] font-semibold text-foreground sm:px-4">
                CLUB PRO
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const isLast = idx === lastIndex;
              return (
                <tr key={`${row.label}-${idx}`}>
                  <td
                    className={`align-middle border-b border-plan-row py-2.5 pr-1.5 sm:pr-2 ${
                      isLast ? "border-b-0" : ""
                    }`}
                  >
                    <span className="flex w-full min-w-0 items-center justify-between gap-1.5 text-left text-[12px] font-medium leading-[1.3] text-foreground">
                      <span className="min-w-0 overflow-wrap-anywhere">{row.label}</span>
                      <Tooltip
                        open={openHelp === idx}
                        onOpenChange={(next) => setOpenHelp(next ? idx : null)}
                      >
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            tabIndex={-1}
                            onPointerDown={(event) => event.preventDefault()}
                            onClick={() => setOpenHelp((current) => (current === idx ? null : idx))}
                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center text-plan-info transition-colors hover:text-foreground"
                            aria-label={`${t("planComparison", "infoAbout")} ${row.label}`}
                          >
                            <Info className="h-3 w-3" strokeWidth={1.5} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          align="start"
                          sideOffset={6}
                          avoidCollisions
                          collisionPadding={12}
                          className="z-[80] max-w-[220px] whitespace-normal rounded-lg border border-plan-popup-border bg-card px-3 py-2.5 text-[11px] leading-snug text-foreground shadow-plan-tooltip"
                        >
                          {row.help}
                        </TooltipContent>
                      </Tooltip>
                    </span>
                  </td>

                  <td
                    className={`overflow-wrap-anywhere align-middle border-b border-plan-row px-1.5 py-2.5 text-center text-[12px] font-normal leading-[1.3] text-muted-foreground sm:px-2 ${
                      isLast ? "border-b-0" : ""
                    }`}
                  >
                    {row.lite}
                  </td>

                  <td
                    className={`overflow-wrap-anywhere align-middle bg-plan-pro-surface px-2 py-2.5 text-center text-[12px] font-semibold leading-[1.3] text-foreground sm:px-4 ${
                      isLast ? "rounded-b-xl" : ""
                    }`}
                  >
                    {row.pro}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <p className="mt-4 text-center text-[14px] leading-[1.45] text-foreground">
          {footer}
        </p>

        <Button
          type="button"
          onClick={() => void handleCheckout()}
          disabled={checkoutLoading}
          className="mt-3 min-h-11 h-auto w-full whitespace-normal rounded-full bg-plan-cta px-4 py-2.5 text-center text-xs font-semibold leading-snug text-primary-foreground hover:bg-plan-cta/90"
        >
          {checkoutLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {content.cta}
        </Button>
      </div>
    </TooltipProvider>
  );
}
